import i18next from "i18next";
import React, { Fragment, useCallback, useState } from "react";
import MailSVG from "resources/icons/mail.svg";
import PlusSVG from "resources/icons/plus.svg";
import TrashSVG from "../../resources/icons/trash.svg";
import css from "./inviteMember.module.scss";
import { Button, Loader } from "components";
import { useDispatch } from "react-redux";
import { setAppModal } from "redux/slices/appSlice";
import { MODALS } from "common";
import invitationService from "services/invite-members.service";
import { fetchMembers } from "redux/slices/teamMembersSlice";
import Utils from "common/utils";

const createUserRow = () => ({
  id: `${Date.now()}-${Math.random()}`,
  email: "",
  roles: "admin",
  status: "idle",
  error: "",
});

const getErrorMessage = (error) =>
  error.response?.data?.message || i18next.t("labels.invite_send_failed");

export default function InviteMember() {
  const dispatch = useDispatch();
  const [users, setUsers] = useState([createUserRow()]);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState("");

  const addNewEmail = useCallback(() => {
    setUsers((currentUsers) => [...currentUsers, createUserRow()]);
    setSummary("");
  }, []);

  const onCancel = useCallback(() => {
    dispatch(setAppModal(MODALS.NONE));
  }, [dispatch]);

  const onSubmit = useCallback(async () => {
    const normalizedUsers = users.map((user) => ({
      ...user,
      email: user.email.trim(),
      error: "",
      status: user.status === "sent" ? "sent" : "idle",
    }));
    const emailCounts = normalizedUsers.reduce((counts, user) => {
      const email = user.email.toLowerCase();
      if (email) counts[email] = (counts[email] || 0) + 1;
      return counts;
    }, {});
    const validatedUsers = normalizedUsers.map((user) => {
      if (user.status === "sent") return user;
      if (!user.email) {
        return { ...user, error: i18next.t("labels.invite_email_required") };
      }
      if (!Utils.validateEmail(user.email)) {
        return { ...user, error: i18next.t("labels.invite_email_invalid") };
      }
      if (emailCounts[user.email.toLowerCase()] > 1) {
        return { ...user, error: i18next.t("labels.invite_duplicate_email") };
      }
      if (!["admin", "readOnly"].includes(user.roles)) {
        return { ...user, error: i18next.t("labels.invite_role_invalid") };
      }
      return user;
    });

    setUsers(validatedUsers);
    setSummary("");

    const validUsers = validatedUsers.filter(
      (user) => !user.error && user.status !== "sent"
    );
    if (validUsers.length === 0) {
      setSummary(i18next.t("labels.invite_no_valid_emails"));
      return;
    }

    setIsLoading(true);
    setUsers((currentUsers) =>
      currentUsers.map((user) =>
        validUsers.some((validUser) => validUser.id === user.id)
          ? { ...user, status: "sending", error: "" }
          : user
      )
    );

    const results = await Promise.all(
      validUsers.map(async (user) => {
        try {
          await invitationService.sendInvite(user);
          return { id: user.id, status: "sent", error: "" };
        } catch (error) {
          return { id: user.id, status: "error", error: getErrorMessage(error) };
        }
      })
    );

    const resultById = new Map(results.map((result) => [result.id, result]));
    setUsers((currentUsers) =>
      currentUsers.map((user) => ({
        ...user,
        ...(resultById.get(user.id) || {}),
      }))
    );

    const sentCount = results.filter((result) => result.status === "sent").length;
    const failedCount = results.length - sentCount;
    const hasValidationErrors = validatedUsers.some((user) => Boolean(user.error));
    if (sentCount > 0) {
      dispatch(fetchMembers({ page: 0, size: 20 }));
    }

    if (failedCount === 0 && !hasValidationErrors) {
      setIsLoading(false);
      dispatch(setAppModal(MODALS.NONE));
      return;
    }
    if (failedCount === 0) {
      setSummary(i18next.t("labels.invite_sent_with_errors", { count: sentCount }));
    } else if (sentCount === 0) {
      setSummary(i18next.t("labels.invite_all_failed", { count: failedCount }));
    } else {
      setSummary(
        i18next.t("labels.invite_some_failed", {
          sent: sentCount,
          failed: failedCount,
        })
      );
    }
    setIsLoading(false);
  }, [users, dispatch]);

  const updateUser = useCallback((index, updates) => {
    setUsers((currentUsers) =>
      currentUsers.map((user, userIndex) =>
        userIndex === index
          ? { ...user, ...updates, status: "idle", error: "" }
          : user
      )
    );
    setSummary("");
  }, []);

  const onDelete = useCallback((index) => {
    setUsers((currentUsers) => currentUsers.filter((_, userIndex) => userIndex !== index));
    setSummary("");
  }, []);

  return (
    <Fragment>
      <div
        className={css.container}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-team-title"
        aria-describedby="invite-team-description"
      >
        <div className={css.header}>
          <div className={css.title} id="invite-team-title">
            {i18next.t("labels.invite_team_member")}
          </div>
          <div className={css["title-supporting"]} id="invite-team-description">
            {i18next.t("paragraphs.add_teammate")}
          </div>
        </div>
        <div className={css.body}>
          <div className={css.inputContainer}>
            <div className={[css.body, css.flex2].join(" ")}>
              <div className={css.columnTitle}>{i18next.t("labels.email_address")}</div>
            </div>
            <div className={[css.body, css.flex1].join(" ")}>
              <div className={css.columnTitle}>{i18next.t("labels.access_level")}</div>
            </div>
          </div>
          <div className={css.usersList}>
            {users.map((user, index) => (
              <div className={css.userRow} key={user.id}>
                <div className={css.inputContainer}>
                  <div className={[css.body, css.flex2].join(" ")}>
                    <div
                      className={`${css["email-input"]} ${user.error ? css.invalid : ""}`}
                    >
                      <img src={MailSVG} alt="" />
                      <input
                        type="email"
                        autoComplete="off"
                        aria-label={`${i18next.t("labels.email_address")} ${index + 1}`}
                        aria-invalid={Boolean(user.error)}
                        aria-describedby={`invite-error-${user.id}`}
                        placeholder={i18next.t("labels.teammate_email_placeholder")}
                        onChange={(event) =>
                          updateUser(index, { email: event.target.value })
                        }
                        value={user.email}
                        disabled={isLoading || user.status === "sent"}
                      />
                    </div>
                    {user.error && (
                      <div className={css.rowError} id={`invite-error-${user.id}`} role="alert">
                        {user.error}
                      </div>
                    )}
                    {user.status === "sent" && (
                      <div className={css.rowSuccess} role="status">
                        {i18next.t("labels.invite_sent")}
                      </div>
                    )}
                  </div>
                  <div className={[css.body, css.flex1].join(" ")}>
                    <div className={css["email-input"]}>
                      <select
                        aria-label={`${i18next.t("labels.access_level")} - ${user.email || index + 1}`}
                        value={user.roles}
                        onChange={(event) => updateUser(index, { roles: event.target.value })}
                        disabled={isLoading || user.status === "sent"}
                      >
                        <option value="admin">{i18next.t("roles.admin")}</option>
                        <option value="readOnly">{i18next.t("roles.read")}</option>
                      </select>
                    </div>
                    {user.status === "sending" && (
                      <div className={css.rowStatus} role="status">
                        {i18next.t("labels.invite_sending")}
                      </div>
                    )}
                  </div>
                  {index !== 0 && (
                    <button
                      type="button"
                      className={css.delete}
                      aria-label={i18next.t("labels.remove_invite_row")}
                      onClick={() => onDelete(index)}
                      disabled={isLoading}
                    >
                      <img src={TrashSVG} alt="" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className={css.add}
            onClick={addNewEmail}
            disabled={isLoading}
          >
            <img src={PlusSVG} alt="" />
            <span>{i18next.t("labels.add_another_team")}</span>
          </button>
          {summary && (
            <div className={css.summary} role="status">{summary}</div>
          )}
          <div className={css.buttons_container}>
            <Button
              title={i18next.t("buttons.cancel")}
              className={css.cancelButton}
              onPress={onCancel}
              disabled={isLoading}
            />
            <Button
              title={i18next.t("buttons.send")}
              className={css.sendButton}
              onPress={onSubmit}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
      {isLoading ? <Loader /> : null}
    </Fragment>
  );
}
